import { useFocusEffect, useRoute } from '@react-navigation/native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { useCart } from '../../hooks/cart/CartProvider';
import React, { useCallback, useMemo, useRef } from 'react';
import { TEN_SECONDS } from '../../lib/constants';
import { ScreenName } from '../../lib/enums';
import PaymentConfirmation from './PaymentConfirmation/PaymentConfirmation';
import PaymentStatus from './PaymentStatus/PaymentStatus';
import { useCartNavigationHelper } from '../../hooks/useCartNavigationHelper/useCartNavigationHelper';
import { useCartSetActiveScreen } from '../../hooks/useCartNavigationHelper/useCartSwitcherEffect';
import useBehaviorSubjectState from '../../hooks/rxjs/useSubjectState';
import { sessionSubject } from '../../state/sessionObservable';
import { paidAmountByRequest } from '../../utils/paidAmountByRequest';

export type PaymentScreenParam = NativeStackScreenProps<
  {
    [ScreenName.PAYMENT]: {
      error?: boolean;
      awaiting?: boolean;
      changeDue?: number;
      amount?: number;
      paymentRequestId?: number;
    };
  },
  ScreenName.PAYMENT
>;

const PaymentScreen: React.FC = () => {
  const cachedPaymentAmount = useRef(0);

  const { order } = useCart();
  const { navigateIdleScreen } = useCartNavigationHelper();
  const { params: routeParams } = useRoute<PaymentScreenParam['route']>();
  const { value: session } = useBehaviorSubjectState(sessionSubject);

  useCartSetActiveScreen(ScreenName.PAYMENT, routeParams);

  const {
    error = false,
    awaiting = false,
    changeDue = 0,
    amount = 0,
    paymentRequestId = '',
  } = routeParams ?? {};

  const device = session?.device as
    | { linkCdsToPayApp?: boolean; enableTap2PayEmbedded?: boolean }
    | undefined;
  const isEmbeddedCdsPayment = !!(
    device?.enableTap2PayEmbedded && device?.linkCdsToPayApp
  );

  const amountByPayment = useMemo(() => {
    const payment = order.payments?.find(
      payment => payment.paymentRequestId === paymentRequestId,
    );
    const paid = payment ? payment.tendered : 0;
    // try to cache paid value for a brief moment because the screen may display but shared order state already changed
    if (paid) {
      cachedPaymentAmount.current = paid;
    }
    return paid;
  }, [paymentRequestId, order]);

  useFocusEffect(
    useCallback(() => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let timeoutId: any;
      if (!awaiting && !error) {
        timeoutId = setTimeout(() => {
          navigateIdleScreen();
        }, TEN_SECONDS);
      }
      return () => {
        timeoutId && clearTimeout(timeoutId);
      };
    }, [navigateIdleScreen, awaiting, error]),
  );

  if (awaiting || error) {
    return (
      <PaymentStatus
        status={
          !awaiting ? 'error' : isEmbeddedCdsPayment ? 'processing' : 'awaiting'
        }
        amountDue={amount || cachedPaymentAmount.current}
        onDismiss={navigateIdleScreen}
      />
    );
  }

  return (
    <PaymentConfirmation
      changeDue={changeDue ?? 0}
      amount={
        amount ||
        amountByPayment ||
        paidAmountByRequest.get(String(paymentRequestId)) ||
        cachedPaymentAmount.current ||
        0
      }
    />
  );
};

export default PaymentScreen;
